// A hash router. <hub> is the hub's Hub ID, never its name, which changes
// when its repository is renamed and which two orgs may share. Pages show the
// name only. See Design > UI, "Studio hierarchy".
//   #/                                   All hubs
//   #/<hub>/overview                     and #/<hub>/overview/<tab>: summary (default) | about | attention
//   #/<hub>/questions                    and #/<hub>/questions/<question id>
//   #/register                           register a hub
//   #/needs-you                          the Interactions waiting on the curator, across every hub
//   #/<hub>/toolsets                     push, stage, test and activate a toolset; the hub's listing
//   #/<hub>/golden                       golden questions, run as test runs
//   #/<hub>/agent                        the hub agent's activity and Interactions
//   #/<hub>/pricing                      pricing signals
//   #/<hub>/reputation
//   #/<hub>/demand/<group>/<days>?q=…    group: declined | outranked | missed
//   #/<hub>/help                         and #/<hub>/help/<FAQ item id>, opened

import { useEffect, useState } from "preact/hooks";
import type { HubId } from "../../../src/exchange/curator-api.ts";
import { DEMAND_DAYS, DEMAND_GROUPS, type DemandGroup } from "../domain/demand.ts";
import type { HubRecord, HubSummary } from "../domain/types.ts";

export const OVERVIEW_TABS = ["summary", "about", "attention"] as const;
export type OverviewTab = typeof OVERVIEW_TABS[number];

/**
 * A page. Studio builds a route to a hub from that hub's record, so its `hub`
 * is a Hub ID; a route read from the URL holds whatever text was there, until
 * `routedHub` finds the hub with that Hub ID.
 */
export type Route<Hub extends string = HubId> =
  | { name: "home" }
  | { name: "overview"; hub: Hub; tab?: OverviewTab }
  | { name: "questions"; hub: Hub }
  | { name: "question"; hub: Hub; intent: string }
  | { name: "register" }
  | { name: "needs-you" }
  | { name: "toolsets"; hub: Hub }
  | { name: "golden"; hub: Hub }
  | { name: "agent"; hub: Hub }
  | { name: "pricing"; hub: Hub }
  | { name: "reputation"; hub: Hub }
  | { name: "demand"; hub: Hub; group: DemandGroup; days: number; search: string }
  | { name: "help"; hub: Hub; item: string }
  | { name: "not-found" };
/** A route as the URL has it. */
export type ParsedRoute = Route<string>;

// The URL is open text, so pages are looked up rather than matched; an unknown one is not-found.
const PAGES = new Map<string, (hub: string, a: string | undefined, b: string | undefined, query: string) => ParsedRoute>([
  ["overview", (hub, a) => {
    const tab = OVERVIEW_TABS.find((t) => t === a);
    return tab ? { name: "overview", hub, tab } : { name: "overview", hub };
  }],
  ["questions", (hub, a) => (a ? { name: "question", hub, intent: a } : { name: "questions", hub })],
  ["toolsets", (hub) => ({ name: "toolsets", hub })],
  ["golden", (hub) => ({ name: "golden", hub })],
  ["agent", (hub) => ({ name: "agent", hub })],
  ["pricing", (hub) => ({ name: "pricing", hub })],
  ["reputation", (hub) => ({ name: "reputation", hub })],
  ["help", (hub, a) => ({ name: "help", hub, item: a ?? "" })],
  ["demand", (hub, a, b, query) => ({
    name: "demand", hub,
    group: DEMAND_GROUPS.find((g) => g === a) ?? "declined",
    days: DEMAND_DAYS.find((d) => d === Number(b)) ?? 30,
    search: new URLSearchParams(query).get("q") ?? "",
  })],
]);

export function parseRoute(hash: string): ParsedRoute {
  const [path = "", query = ""] = hash.replace(/^#\/?/, "").split("?");
  const parts = path.split("/").filter(Boolean).map((p) => decodeURIComponent(p));
  const [hub, page, a, b] = parts;
  if (!hub) return { name: "home" };
  if (hub === "register" && !page) return { name: "register" };
  if (hub === "needs-you" && !page) return { name: "needs-you" };
  return PAGES.get(page ?? "overview")?.(hub, a, b, query) ?? { name: "not-found" };
}

/**
 * The hub a route names, among the caller's hubs, and its record: a hub
 * without a toolset yet has only its record. A hub is found by its Hub ID alone, so two hubs with
 * the same name in different orgs are two pages, and a name in the URL finds
 * no hub.
 */
export function routedHub(route: ParsedRoute, hubs: readonly HubSummary[], records: readonly HubRecord[]) {
  const id = "hub" in route ? route.hub : undefined;
  const hub = hubs.find((h) => h.hub.id === id);
  return { hub, record: hub?.hub ?? records.find((r) => r.id === id) };
}

const seg = (s: string) => encodeURIComponent(s);
export function href(route: Route): string {
  switch (route.name) {
    case "home": case "not-found": return "#/";
    case "overview": return `#/${seg(route.hub)}/overview${route.tab && route.tab !== "summary" ? `/${route.tab}` : ""}`;
    case "questions": return `#/${seg(route.hub)}/questions`;
    case "question": return `#/${seg(route.hub)}/questions/${seg(route.intent)}`;
    case "register": return "#/register";
    case "needs-you": return "#/needs-you";
    case "toolsets": return `#/${seg(route.hub)}/toolsets`;
    case "golden": return `#/${seg(route.hub)}/golden`;
    case "agent": return `#/${seg(route.hub)}/agent`;
    case "pricing": return `#/${seg(route.hub)}/pricing`;
    case "reputation": return `#/${seg(route.hub)}/reputation`;
    case "help": return `#/${seg(route.hub)}/help${route.item ? `/${seg(route.item)}` : ""}`;
    case "demand": return `#/${seg(route.hub)}/demand/${route.group}/${route.days}${route.search ? `?q=${encodeURIComponent(route.search)}` : ""}`;
  }
}

/** Goes to a route. `replace` swaps the current history entry, for redirects and search edits. */
export const navigate = (route: Route, replace = false) => { if (replace) location.replace(href(route)); else location.hash = href(route); };

export function useRoute(): ParsedRoute {
  const [route, setRoute] = useState(() => parseRoute(location.hash));
  useEffect(() => {
    const on = () => setRoute(parseRoute(location.hash));
    addEventListener("hashchange", on);
    return () => removeEventListener("hashchange", on);
  }, []);
  return route;
}
